/**
 * types.ts — contratos del feature reports, tipados contra la respuesta REAL
 * del API (entidad Informe, campos en español). Las fechas viajan como ISO string.
 */

export type TipoInforme = "IE" | "IS" | "AC";
export type EstadoInforme = "borrador" | "en_cola" | "sincronizado" | "enviado";

export interface Informe {
  id: string;
  tipo: TipoInforme;
  numero: string;
  cliente: string;
  clienteId: string | null;
  estado: EstadoInforme;
  autorId: string;
  /** Name of the technician who created the report — populated via backend JOIN. */
  autorNombre?: string | null;
  parentId: string | null;
  payload: Record<string, unknown>;
  pdfUrl: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Filtros aceptados por GET /informes. */
export interface InformeFiltros {
  tipo?: TipoInforme;
  estado?: EstadoInforme;
  /** ISO timestamp — only return reports updated after this date (incremental sync). */
  since?: string;
}

/**
 * Cliente — fila del catálogo /clientes, usada por el combobox find-or-create.
 *
 * Coincide estructuralmente con el Cliente del admin-panel. Se declara acá a
 * propósito: reports NO importa de admin-panel (features desacoplados); la
 * duplicación estructural de un tipo plano es aceptable.
 */
export interface Cliente {
  id: string;
  nombre: string;
  sigla: string;
  activo: boolean;
  createdAt: string;
}

/**
 * Entrada para crear un informe (POST /informes). Se envía EITHER `clienteId`
 * (cliente existente) OR `cliente` (nombre nuevo → el backend hace find-or-create).
 */
export interface NuevoInformeInput {
  tipo: TipoInforme;
  clienteId?: string;
  cliente?: string;
  parentId?: string | null;
  /** Inherited payload for child reports (IE→IS/AC, IS→AC). Omitted for standalone creates. */
  payload?: Record<string, unknown>;
}

/**
 * Payload tipado del informe IE. Los nombres de campo coinciden EXACTAMENTE con
 * packages/domain (no traducir ni renombrar). Las fechas viajan como ISO string.
 */
export interface IeDatosGenerales {
  cliente: string;
  atencion: string;
  supervisor: string;
  /** Cargo del supervisor; default DEFAULT_CARGO, editable. Aparece en el PDF (cierre). */
  cargo: string;
  fechaInicio: string;
  fechaCierre: string;
  referencia: string;
  lugar: string;
  ocCliente: string;
  otMpsa: string;
  emision: string;
  anexo: string;
  revision: number;
}

export interface IeEquipo {
  item: number;
  equipo: string;
  marca: string;
  modelo: string;
  serie: string;
  designacion: string;
}

export interface IeFoto {
  url: string;
  titulo: string;
  descripcion: string;
  /**
   * True when the slot's image is portrait (vertical). Panel cards must stay
   * uniform, so the wizard blocks advancing while any slot is vertical. Measured
   * client-side on upload/render; not required by the PDF or the API.
   */
  vertical?: boolean;
}

export interface IeProblema {
  descripcion: string;
  recomendacion: string;
}

export interface IePayload {
  datosGenerales: IeDatosGenerales;
  equipos: IeEquipo[];
  panelFotografico: IeFoto[];
  problemas: IeProblema[];
}

/**
 * Payload tipado del informe IS (Informe de Servicio). Comparte `datosGenerales`
 * y `equipos` con el IE (estructuralmente idénticos en packages/domain), por eso
 * reutiliza `IeDatosGenerales`/`IeEquipo` mediante alias en lugar de duplicarlos.
 * Los nombres de campo coinciden EXACTAMENTE con packages/domain.
 */
export interface IsActividad {
  descripcion: string;
  /** URL de la foto de la actividad (opcional, subida a Cloudinary). */
  fotoUrl?: string;
}

/**
 * PR-D (ADR-D1): generic engrase table types.
 * Old IsInyector / IsZonaInyectores kept as @deprecated for legacy read tolerance.
 */
export interface TablaColumna {
  id: string;
  titulo: string;
}

export interface TablaGenerica {
  columnas: TablaColumna[];
  filas: Record<string, string>[];
}

/** A single user-managed zone in the engrase system. */
export interface ZonaEngrase {
  titulo: string;
  descripcion: string;
  tabla: TablaGenerica;
}

export interface IsEngrase {
  tiempoReposo: string; // replaces frecuenciaLubricacion (REQ-10)
  tiempoLubricacion: string;
  tipoPresion: "neumatico" | "hidraulico" | "electrico";
  presionTrabajo: string;
  presionLubricacion: string;
  zonas: ZonaEngrase[];
}

/** @deprecated Use TablaColumna/TablaGenerica. Kept for legacy read tolerance only. */
export interface IsInyector {
  nombre: string;
  dosificacion: string;
}

/** @deprecated Use TablaGenerica. Kept for legacy read tolerance only. */
export interface IsZonaInyectores {
  nombre: string;
  inyectores: IsInyector[];
}

// REQ-8a: antecedentes field removed from IsPayload (S-8a.1, S-8a.2).
export interface IsPayload {
  datosGenerales: IeDatosGenerales;
  equipos: IeEquipo[];
  panelFotografico: IeFoto[];
  actividades: IsActividad[];
  conclusionesRecomendaciones: string[];
  tipoServicio: "engrase" | "otro";
  /** Presente solo cuando `tipoServicio === "engrase"`. */
  engrase?: IsEngrase;
}

/**
 * Payload tipado del Acta de Conformidad (AC). Reutiliza `datosGenerales`,
 * `equipos` y `actividades` de IE/IS (estructuralmente idénticos en
 * packages/domain). Las firmas son referencias a imágenes (URLs de Cloudinary).
 */
export interface AcFirmas {
  /** URL de la firma del técnico (requerida). */
  tecnico: string;
  /** URL de la firma del cliente (opcional). */
  cliente?: string;
}

export interface AcPayload {
  datosGenerales: IeDatosGenerales;
  equipos: IeEquipo[];
  actividades: IsActividad[];
  conclusionesRecomendaciones: string[];
  firmas: AcFirmas;
}

export const TIPO_LABEL: Record<TipoInforme, string> = {
  IE: "Informe de Evaluación",
  IS: "Informe de Servicio",
  AC: "Acta de Conformidad",
};

export const ESTADO_LABEL: Record<EstadoInforme, string> = {
  borrador: "Borrador",
  en_cola: "En cola",
  sincronizado: "Sincronizado",
  enviado: "Enviado",
};

/** Transiciones de estado permitidas (espejo de domain/services/estado.ts). */
export const SIGUIENTES_ESTADOS: Record<EstadoInforme, readonly EstadoInforme[]> = {
  borrador: ["en_cola"],
  en_cola: ["sincronizado", "borrador"],
  sincronizado: ["enviado"],
  enviado: [],
};
